import { useLoadoutStore } from '../store/loadoutSlice';
import { useInventoryStore } from '../store/inventorySlice';
import { useChestStore } from '../store/chestSlice';
import {
  TRINKET_MAJOR_REGISTRY,
  TRINKET_MINOR_REGISTRY,
} from '../data/trinkets.config';
import { EFFECT_HANDLERS } from '../effect-system/effectHandlers';
import { audioService } from './AudioService';
import type { CraftCost } from '../types/craft.types';
import type {
  TrinketId,
  TrinketLevel,
  TrinketMajorDefinition,
  TrinketMinorDefinition,
} from '../types/trinket.types';

function totalAvailable(itemId: string): number {
  // Gold lives in dedicated counters (inventory + chest), not in slots.
  if (itemId === 'gold') {
    return useInventoryStore.getState().gold + useChestStore.getState().gold;
  }
  const inv = useInventoryStore.getState().slots;
  const chest = useChestStore.getState().slots;
  let total = 0;
  for (const s of inv) {
    if (s?.itemId === itemId) {
      total += s.quantity;
    }
  }
  for (const s of chest) {
    if (s.itemId === itemId) {
      total += s.quantity;
    }
  }
  return total;
}

export function canAfford(cost: CraftCost): boolean {
  if (totalAvailable('gold') < cost.gold) {
    return false;
  }
  for (const [matId, qty] of Object.entries(cost.materials)) {
    if (totalAvailable(matId) < qty) {
      return false;
    }
  }
  return true;
}

function consumeResource(itemId: string, qty: number): boolean {
  let remaining = qty;
  const inv = useInventoryStore.getState();
  if (itemId === 'gold') {
    const fromInv = Math.min(inv.gold, remaining);
    inv.spendGold(fromInv);
    remaining -= fromInv;
    if (remaining <= 0) {
      return true;
    }
    return useChestStore.getState().removeGold(remaining);
  }
  const invRemoved = inv.removeItem(itemId, remaining);
  remaining -= invRemoved;
  if (remaining <= 0) {
    return true;
  }
  return useChestStore.getState().removeItem(itemId, remaining);
}

// Atomic: checks `canAfford` first, then consumes. Returns `false` with no
// mutation if resources are insufficient.
export function consume(cost: CraftCost): boolean {
  if (!canAfford(cost)) {
    return false;
  }
  consumeResource('gold', cost.gold);
  for (const [matId, qty] of Object.entries(cost.materials)) {
    consumeResource(matId, qty);
  }
  return true;
}

function baselineLevelUpCost(targetLevel: TrinketLevel): CraftCost {
  return { gold: 100 * targetLevel, materials: {} };
}

function isMajorTrinketDef(
  def: TrinketMajorDefinition | TrinketMinorDefinition,
): def is TrinketMajorDefinition {
  return 'basicAttack' in def;
}

// Affection material baked into every trinket level-up: minors consume soul shards
// (`soul-shard`), majors consume the bigger spirit hearts (`spirit-heart`).
// Linear ramp `targetLevel - 1` (1 at level 2, +1 per level). cf. docs/trinkets.md.
function affectionCost(
  def: TrinketMajorDefinition | TrinketMinorDefinition,
  targetLevel: TrinketLevel,
): { itemId: string; qty: number } {
  return {
    itemId: isMajorTrinketDef(def) ? 'spirit-heart' : 'soul-shard',
    qty: targetLevel - 1,
  };
}

export function computeTrinketLevelUpCost(
  def: TrinketMajorDefinition | TrinketMinorDefinition,
  currentLevel: TrinketLevel,
): CraftCost | null {
  const targetLevel = currentLevel + 1;
  if (targetLevel > def.maxLevel) {
    return null;
  }
  const base =
    def.levelUpCostOverrides?.[targetLevel] ?? baselineLevelUpCost(targetLevel);
  const { itemId, qty } = affectionCost(def, targetLevel);
  const materials = { ...base.materials };
  // Never clobber an explicit per-trinket override for the same material.
  if (qty > 0 && materials[itemId] === undefined) {
    materials[itemId] = qty;
  }
  return { ...base, materials };
}

function handleTrinketLevelUpEffects(
  def: TrinketMajorDefinition | TrinketMinorDefinition,
  newLevel: number,
): void {
  if (!def.effectsByLevel) {
    return;
  }
  const effects = def.effectsByLevel[newLevel];
  if (!effects?.onUnlock) {
    return;
  }
  // onUnlock handlers fire outside any combat context — caster is irrelevant.
  // ctx is artificial; handlers like `unlockMinor` ignore it entirely.
  const ctx = {
    caster: null as any,
    direction: 0,
    affectedCells: [],
  };
  for (const step of effects.onUnlock) {
    const handler = EFFECT_HANDLERS[step.handler];
    if (!handler) {
      continue;
    }
    handler(ctx, { ...step.params });
  }
}

export function levelUpTrinket(modelId: TrinketId): boolean {
  const store = useLoadoutStore.getState();
  const level = store.getMajorLevel(modelId) ?? store.getMinorLevel(modelId);
  if (level === null) {
    return false;
  }
  const def =
    TRINKET_MAJOR_REGISTRY[modelId] ?? TRINKET_MINOR_REGISTRY[modelId];
  if (!def) {
    return false;
  }
  const cost = computeTrinketLevelUpCost(def, level);
  if (!cost) {
    return false;
  }
  if (!consume(cost)) {
    return false;
  }
  audioService.playSfx('levelup');
  store.setTrinketLevel(modelId, level + 1);
  handleTrinketLevelUpEffects(def, level + 1);
  return true;
}
